import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { documentHref } from '@/lib/chat-sources';
import { listKnowledgeTags, loadDocumentTags } from '@/lib/knowledge-tags';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import type { KnowledgeDocument } from '@shared/types/knowledge';
import {
  deleteKnowledgeDocument,
  listFolderDocuments,
  uploadKnowledgeFileDocument,
  uploadKnowledgeInlineDocument,
} from '@/lib/knowledge';

interface KnowledgeFolderDetailsProps {
  folderId: string;
  folderName: string;
  userId: string;
  onDocumentCountChange?: (delta: number) => void;
}

type AddMode = 'file' | 'inline';

export const KnowledgeFolderDetails = ({
  folderId,
  folderName,
  userId,
  onDocumentCountChange,
}: KnowledgeFolderDetailsProps) => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  // D4: labels shown under each document (set in its drawer).
  const [tagsByDocument, setTagsByDocument] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<AddMode>('file');
  const [title, setTitle] = useState('');
  const [inlineContent, setInlineContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const docs = await listFolderDocuments(folderId);
      setDocuments(docs);
      const [tags, byDocument] = await Promise.all([listKnowledgeTags(), loadDocumentTags(docs.map((doc) => doc.id))]);
      const nameOf = new Map(tags.map((tag) => [tag.id, tag.name]));
      setTagsByDocument(Object.fromEntries(Object.entries(byDocument).map(([id, tagIds]) => [id, tagIds.flatMap((tagId) => nameOf.get(tagId) ?? [])])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  const resetForm = () => {
    setTitle('');
    setInlineContent('');
    setFile(null);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Donne un titre au document.');
      return;
    }
    if (mode === 'inline' && !inlineContent.trim()) {
      setError('Le contenu du document est vide.');
      return;
    }
    if (mode === 'file' && !file) {
      setError('Sélectionne un fichier à uploader.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'inline') {
        await uploadKnowledgeInlineDocument({
          folderId,
          title: title.trim(),
          content: inlineContent,
        });
      } else if (file) {
        await uploadKnowledgeFileDocument({
          folderId,
          title: title.trim(),
          file,
          userId,
        });
      }
      onDocumentCountChange?.(1);
      resetForm();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Supprimer ce document ?')) return;
    try {
      await deleteKnowledgeDocument(docId);
      onDocumentCountChange?.(-1);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Suppression impossible');
    }
  };

  const formatSize = (bytes: number | null): string => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="border-t border-gray-100 mt-3 pt-3 space-y-3">
      <div className="text-xs text-gray-500">
        Documents du dossier <span className="font-medium text-gray-700">{folderName}</span>
      </div>

      {loading ? (
        <div className="text-xs text-gray-400">Chargement…</div>
      ) : documents.length === 0 ? (
        <div className="text-xs text-gray-400 italic">Aucun document pour l'instant.</div>
      ) : (
        <ul className="space-y-2">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex items-start justify-between gap-3 px-3 py-2 rounded-md bg-gray-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link to={documentHref(doc.id)} className="text-sm font-medium text-gray-900 truncate hover:text-domino-aubergine">{doc.title}</Link>
                  {(tagsByDocument[doc.id] ?? []).map((name) => (
                    <span key={name} className="text-xs text-domino-aubergine">#{name}</span>
                  ))}
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">
                    {doc.source_kind === 'upload'
                      ? 'Fichier'
                      : doc.source_kind === 'drive_ref'
                        ? 'Drive'
                        : 'Texte'}
                  </span>
                </div>
                {doc.summary ? (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{doc.summary}</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1 italic">Résumé en cours…</p>
                )}
                <div className="text-[11px] text-gray-400 mt-1">
                  {doc.char_count.toLocaleString('fr-FR')} caractères
                  {doc.byte_size ? ` · ${formatSize(doc.byte_size)}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleDelete(doc.id)}
                className="text-xs text-gray-400 hover:text-red-600"
              >
                Supprimer
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 border-t border-gray-100 pt-3 space-y-3">
        <div className="text-xs font-medium text-gray-700">Ajouter un document</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('file')}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              mode === 'file'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Fichier (PDF, MD, TXT)
          </button>
          <button
            type="button"
            onClick={() => setMode('inline')}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              mode === 'inline'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Coller du texte
          </button>
        </div>
        <Input
          placeholder="Titre du document"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {mode === 'file' ? (
          <input
            type="file"
            accept="application/pdf,text/plain,text/markdown,.md,.txt"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-xs text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-gray-900 file:px-3 file:py-1.5 file:text-white"
          />
        ) : (
          <Textarea
            placeholder="Colle ici le contenu du document…"
            rows={6}
            value={inlineContent}
            onChange={(e) => setInlineContent(e.target.value)}
          />
        )}

        {error ? <div className="text-xs text-red-600">{error}</div> : null}

        <Button size="sm" onClick={() => void handleSubmit()} loading={submitting} disabled={submitting}>
          Ajouter au dossier
        </Button>
      </div>
    </div>
  );
};
